import { ORPCError } from '@orpc/client';

/**
 * `<input type="number">` hands react-hook-form a string, and an empty input hands it `''`.
 * `z.coerce.number()` would read `''` as `0`, so blanks are turned back into `undefined` before the
 * resolver ever sees them — an untouched optional field must stay absent, not become zero.
 */
export const numberFieldOptions = {
  setValueAs: (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);

    return Number.isNaN(parsed) ? undefined : parsed;
  },
} as const;

/** Same idea for optional text: an empty input means "not set", not an empty string. */
export const textFieldOptions = {
  setValueAs: (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
      return value === null || value === undefined ? undefined : String(value);
    }

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
  },
} as const;

/** Optional text that has to be clearable on a PATCH: an emptied input sends an explicit `null`. */
export const clearableTextFieldOptions = {
  setValueAs: (value: unknown): string | null => {
    if (typeof value !== 'string') {
      return value === null || value === undefined ? null : String(value);
    }

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
  },
} as const;

export const isConflictError = (error: unknown): boolean => error instanceof ORPCError && error.status === 409;

export const isForbiddenError = (error: unknown): boolean => error instanceof ORPCError && error.status === 403;

/**
 * Turns a failed mutation into the right i18n key. A 409 and a 403 are the two outcomes a user can act
 * on; everything else is the generic save failure.
 */
export const submitErrorKey = (error: unknown): string => {
  if (isConflictError(error)) {
    return 'errors.conflict';
  }

  if (isForbiddenError(error)) {
    return 'errors.managerOnly';
  }

  return 'errors.saveFailed';
};

/** Prefers the service's own message (the 409s name the offending row) over the generic fallback. */
export const serverMessage = (error: unknown): string | null => {
  if (!(error instanceof ORPCError)) {
    return null;
  }

  const data = error.data as { message?: unknown } | undefined;

  if (data && typeof data.message === 'string' && data.message.trim() !== '') {
    return data.message;
  }

  return typeof error.message === 'string' && error.message.trim() !== '' ? error.message : null;
};
